import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { MayaMark } from "@/components/site/BrandLockup";
import es from "@/assets/guides/monte-alban-es.mp3.asset.json";
import en from "@/assets/guides/monte-alban-en.mp3.asset.json";
import fr from "@/assets/guides/monte-alban-fr.mp3.asset.json";
import it from "@/assets/guides/monte-alban-it.mp3.asset.json";
import de from "@/assets/guides/monte-alban-de.mp3.asset.json";
import pt from "@/assets/guides/monte-alban-pt.mp3.asset.json";

type Lang = "es" | "en" | "fr" | "it" | "de" | "pt";
const AUDIO: Record<Lang, { url: string }> = { es, en, fr, it, de, pt };
const NAMES: Record<Lang, string> = { es: "Español", en: "English", fr: "Français", it: "Italiano", de: "Deutsch", pt: "Português" };
const T: Record<Lang, { title: string; sub: string; dl: string }> = {
  es: { title: "Audioguía de Monte Albán", sub: "Escucha con la voz de Maya la historia, geografía, gastronomía y leyendas de la antigua capital zapoteca de Oaxaca.", dl: "Descargar audio" },
  en: { title: "Monte Albán audio guide", sub: "Listen to Maya narrate the history, geography, food and legends of Oaxaca's ancient Zapotec capital.", dl: "Download audio" },
  fr: { title: "Audioguide de Monte Albán", sub: "Écoute Maya raconter l'histoire, la géographie, la gastronomie et les légendes de l'ancienne capitale zapotèque d'Oaxaca.", dl: "Télécharger l'audio" },
  it: { title: "Audioguida di Monte Albán", sub: "Ascolta Maya raccontare storia, geografia, gastronomia e leggende dell'antica capitale zapoteca di Oaxaca.", dl: "Scarica l'audio" },
  de: { title: "Audioguide Monte Albán", sub: "Hör Maya zu: Geschichte, Geografie, Küche und Legenden der alten Zapotekenhauptstadt Oaxacas.", dl: "Audio herunterladen" },
  pt: { title: "Audioguia de Monte Albán", sub: "Ouça a Maya narrar a história, geografia, gastronomia e lendas da antiga capital zapoteca de Oaxaca.", dl: "Baixar áudio" },
};
const LANGS = Object.keys(NAMES) as Lang[];

export const Route = createFileRoute("/guia/monte-alban")({
  head: () => ({
    meta: [
      { title: "Audioguía de Monte Albán, Oaxaca | Mostrando México" },
      { name: "description", content: "Audioguía gratuita de Monte Albán narrada por Maya en seis idiomas: historia zapoteca, geografía, gastronomía y leyendas." },
      { property: "og:title", content: "Audioguía de Monte Albán | Mostrando México" },
      { property: "og:description", content: "Escucha y descarga la audioguía de Monte Albán narrada por Maya en seis idiomas." },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

function Page() {
  const { i18n } = useTranslation();
  const [lang, setLang] = useState<Lang>("es");
  useEffect(() => {
    const l = i18n.language.slice(0, 2) as Lang;
    if (LANGS.includes(l)) setLang(l);
  }, [i18n.language]);
  const t = T[lang];
  return (
    <main className="mx-auto max-w-2xl px-4 py-10">
      <div className="flex items-center gap-3">
        <MayaMark size={52} />
        <h1 className="font-display text-2xl font-semibold sm:text-3xl">{t.title}</h1>
      </div>
      <p className="mt-3 text-muted-foreground">{t.sub}</p>
      <div className="mt-6 flex flex-wrap gap-2" role="tablist">
        {LANGS.map((l) => (
          <button key={l} type="button" onClick={() => setLang(l)} aria-pressed={lang === l}
            className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${lang === l ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:border-primary/40"}`}>
            {NAMES[l]}
          </button>
        ))}
      </div>
      <audio key={lang} controls preload="none" src={AUDIO[lang].url} className="mt-5 w-full" />
      <a href={AUDIO[lang].url} download={`monte-alban-${lang}.mp3`}
        className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary/40 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10">
        <Download className="h-4 w-4" /> {t.dl}
      </a>
    </main>
  );
}
